import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { useEffect, useState } from "react";
import { fetchBranches } from "../api/branchApi"; // API for fetching branches
import { useTranslation } from "react-i18next";
import { FaBuilding, FaClock, FaDirections, FaEnvelope, FaMapMarkerAlt, FaPhone } from "react-icons/fa";
import i18next from "i18next";

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = { lat: 25.2089363, lng: 51.4922803};
const headOffice = {
  name: "Head Office",
  address: "INDUSTRIAL AREA 12, Doha, Qatar",
  mobile: "+974 70205550",
  email: "info@gocleans.co",
  google_map_url: "https://maps.google.com/?q=25.2089363,51.4922803",
};

const BranchList = () => {
   const [selectedBranch, setSelectedBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [markerPosition, setMarkerPosition] = useState(defaultCenter);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
  const {t}= useTranslation();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyCzamSDosuZw19VnkrOGRF4ZrglHWBMmx8",
  });

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };


   useEffect(() => {
    async function getBranches() {
      try {
        const data = await fetchBranches();
        const formattedBranches = data.map((branch) => ({
          ...branch,
          open_hours: formatTime(branch.open_hours),
          close_hours: formatTime(branch.close_hours),
          coordinates: JSON.parse(branch.google_map_url),
        }));
        setBranches(formattedBranches);
        
        if (formattedBranches.length > 0) {
          setSelectedBranch(formattedBranches[0]);
          setMapCenter(formattedBranches[0].coordinates);
        } else {
          // If no branches, show head office
          setSelectedBranch(headOffice);
          setMapCenter(headOffice.coordinates);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
        // Fallback to head office
        setSelectedBranch(headOffice);
        setMapCenter(headOffice.coordinates);
      }
    }
    getBranches();
  }, []);

    const handleBranchSelect = (branch) => {
    setSelectedBranch(branch);
    setMapCenter(branch.coordinates);
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-2xl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#66B8D9] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  // If only head office (no branches), show simplified view
  const showOnlyHeadOffice = branches.length === 0;

  return (
     <div  dir={i18next.language === "ar" ? "rtl" : "ltr"} className=" rounded-xl  overflow-hidden w-full px-4 mb-3">
      
      <div className="bg-gradient-to-r  p-6">
        <h2 className="text-2xl font-bold text-black flex items-center">
          <FaBuilding className="mr-3 rtl:ml-3 rtl:mr-0" />
          {showOnlyHeadOffice ? t('headquarters') : t('branches')}
        </h2>          

      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Left Panel - Branch List (hidden if only head office) */}
        {!showOnlyHeadOffice && (
          <div className="w-full lg:w-1/3  border-r border-gray-200">
            <div className="p-6">

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {branches.map((branch) => (
                  <div
                    key={branch.branch_id}
                    className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                      selectedBranch?.branch_id === branch.branch_id
                        ? 'bg-[#66B8D9] text-white shadow-lg scale-[1.02]'
                        : 'bg-white text-gray-700 hover:bg-gray-100 hover:shadow-md'
                    }`}
                    onClick={() => handleBranchSelect(branch)}
                  >
                    <h4 className="font-semibold text-lg mb-2">{branch.name}</h4>
                    <div className="flex items-center text-sm mb-2">
                      <FaMapMarkerAlt className="mr-2 flex-shrink-0 rtl:ml-3 rtl:mr-0" />
                      <span className="line-clamp-2">{branch.location}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <FaClock className="mr-2 flex-shrink-0 rtl:ml-3 rtl:mr-0" />
                      <span>{branch.open_hours} - {branch.close_hours}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Right Panel - Map and Details */}
        <div className={`${showOnlyHeadOffice ? 'w-full' : 'w-full lg:w-2/3'} flex flex-col`}>
          {/* Map */}
          <div className="h-80 lg:h-96 relative">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={13}
              center={mapCenter}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                styles: [
                  {
                    featureType: "poi",
                    elementType: "labels",
                    stylers: [{ visibility: "off" }]
                  }
                ]
              }}
            >
              {/* Show all branch markers */}
              {branches.map((branch) => (
                <Marker
                  key={branch.branch_id}
                  position={branch.coordinates}
                  title={branch.name}
                  onClick={() => handleBranchSelect(branch)}
                />
              ))}
              
              {/* Show head office marker if no branches */}
              {showOnlyHeadOffice && (
                <Marker
                  position={headOffice.coordinates}
                  title={headOffice.name}
                />
              )}
            </GoogleMap>
          </div>

          {/* Contact Details */}
          <div className="p-6 bg-white">
            {selectedBranch ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{selectedBranch.name}</h3>
                  <a
                    href={`https://maps.google.com/?q=${selectedBranch.coordinates?.lat},${selectedBranch.coordinates?.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-[#66B8D9] text-white rounded-lg hover:bg-[#4E98B9] transition-colors text-sm font-medium"
                  >
                    <FaDirections className="mr-2 rtl:ml-3 rtl:mr-0" />
                    {t('getdirections')}
                  </a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <FaMapMarkerAlt className="mr-3 mt-1 text-[#66B8D9] flex-shrink-0 rtl:ml-3 rtl:mr-0" />
                      <div>
                        <p className="font-medium text-gray-900">Address</p>
                        <p className="text-gray-600 text-sm">{selectedBranch.address || selectedBranch.location}</p>
                      </div>
                    </div>

                    {selectedBranch.phone && (
                      <div className="flex items-center">
                        <FaPhone className="mr-3 text-[#66B8D9] flex-shrink-0 rtl:ml-3 rtl:mr-0" />
                        <div>
                          <p className="font-medium text-gray-900">Phone</p>
                          <a href={`tel:${selectedBranch.phone}`} className="text-[#66B8D9] hover:underline text-sm">
                            {selectedBranch.phone}
                          </a>
                        </div>
                      </div>
                    )}

                    {selectedBranch.mobile && (
                      <div className="flex items-center">
                        <FaPhone className="mr-3 text-[#66B8D9] flex-shrink-0 rtl:ml-3 rtl:mr-0" />
                        <div>
                          <p className="font-medium text-gray-900">Mobile</p>
                          <a href={`tel:${selectedBranch.mobile}`} className="text-[#66B8D9] hover:underline text-sm">
                            {selectedBranch.mobile}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    {(selectedBranch.open_hours && selectedBranch.close_hours) && (
                      <div className="flex items-center">
                        <FaClock className="mr-3 text-[#66B8D9] flex-shrink-0 rtl:ml-3 rtl:mr-0" />
                        <div>
                          <p className="font-medium text-gray-900">Operating Hours</p>
                          <p className="text-gray-600 text-sm">{selectedBranch.open_hours} - {selectedBranch.close_hours}</p>
                        </div>
                      </div>
                    )}

                    {selectedBranch.email && (
                      <div className="flex items-center">
                        <FaEnvelope className="mr-3 text-[#66B8D9] flex-shrink-0 rtl:ml-3 rtl:mr-0" />
                        <div>
                          <p className="font-medium text-gray-900">Email</p>
                          <a href={`mailto:${selectedBranch.email}`} className="text-[#66B8D9] hover:underline text-sm">
                            {selectedBranch.email}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <FaMapMarkerAlt className="mx-auto h-12 w-12 text-gray-400 mb-4 rtl:ml-3 rtl:mr-0" />
                <p className="text-gray-600">{t('selectbranch')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchList;
